import { supabase } from '../lib/supabase';
import { Squad, SessionDetails, SessionSummaryStats, emptySquadAttendance, emptySquadCoaches, emptySquadFocus } from '../types';

// Local types for Supabase join result shapes
type SquadRef = { id: string; name: string; display_name: string };
type SessionSquadJoin = { id?: string; squad_id: string; squads: SquadRef; focus?: string | null };
type CoachRef = { id: string; name: string } | null;
type SessionCoachJoin = { squad_id: string; primary_coach_id: string | null; secondary_coach_id: string | null; primary_coach: CoachRef; secondary_coach: CoachRef; squads: SquadRef };
type AttendanceJoin = { id: string; player_id: string; squad_id: string; present: boolean; comments?: string | null; players: { id: string; name: string } };

// Get session summary statistics
export const getSessionSummaryStats = async (sessionId: string): Promise<SessionSummaryStats> => {
  try {
    // Get session with all related data
    const { data: session, error } = await supabase
      .from('sessions')
      .select(`
        *,
        session_squads (
          id,
          squad_id,
          squads (
            id,
            name,
            display_name
          ),
          focus
        ),
        session_coaches (
          squad_id,
          primary_coach:coaches!primary_coach_id (
            id,
            name
          ),
          secondary_coach:coaches!secondary_coach_id (
            id,
            name
          ),
          squads (
            id,
            name,
            display_name
          )
        ),
        attendance (
          id,
          player_id,
          squad_id,
          present,
          comments,
          players (
            id,
            name
          )
        ),
        documents (
          id,
          name,
          type,
          url,
          size,
          uploaded_at
        )
      `)
      .eq('id', sessionId)
      .single();

    if (error) {
      console.error('Error fetching session summary:', error);
      throw error;
    }

    if (!session) {
      throw new Error('Session not found');
    }

    const sessionSquads = session.session_squads as SessionSquadJoin[];
    const sessionCoaches = session.session_coaches as SessionCoachJoin[];
    const sessionAttendance = (session.attendance ?? []) as AttendanceJoin[];

    // Get all players for each squad
    const squadIds = sessionSquads.map(ss => ss.squad_id);
    const { data: players, error: playersError } = await supabase
      .from('players')
      .select('id, name, squad_id')
      .in('squad_id', squadIds)
      .eq('subscription_status', 'Active');

    if (playersError) {
      console.error('Error fetching players:', playersError);
      throw playersError;
    }

    // Calculate stats for each squad
    const squadStats = sessionSquads.map(ss => {
      const squadPlayers = players?.filter(p => p.squad_id === ss.squad_id) ?? [];
      const totalPlayers = squadPlayers.length;

      const squadAttendance = sessionAttendance.filter(a => a.squad_id === ss.squad_id);
      const present = squadAttendance.filter(a => a.present).length;
      const absent = totalPlayers - present;
      const attendancePercentage = totalPlayers > 0 ? (present / totalPlayers) * 100 : 0;

      const squadCoaches = sessionCoaches.find(sc => sc.squad_id === ss.squad_id);

      return {
        squad: ss.squads.name,
        displayName: ss.squads.display_name,
        attendance: {
          present,
          absent,
          total: totalPlayers,
          percentage: attendancePercentage
        },
        coaches: {
          primary: squadCoaches?.primary_coach?.name || 'No Coach Assigned',
          secondary: squadCoaches?.secondary_coach?.name || 'None'
        },
        players: squadAttendance.map(a => ({
          id: a.player_id,
          name: a.players.name,
          present: a.present,
          comments: a.comments
        }))
      };
    });

    // Process documents and get signed URLs
    const documents = await Promise.all(
      (session.documents || []).map(async (doc: any) => {
        try {
          // Skip invalid document URLs
          if (!doc.url || doc.url.startsWith('blob:')) {
            console.warn('Invalid document URL:', doc.url);
            return null;
          }

          // Check if the document exists in storage
          const { data: exists, error: existsError } = await supabase
            .storage
            .from('mbfc-data')
            .list(doc.url.split('/').slice(0, -1).join('/'));

          if (existsError || !exists?.length) {
            console.warn('Document not found in storage:', doc.url);
            return null;
          }

          // Get signed URL for the document
          const { data, error: signedUrlError } = await supabase
            .storage
            .from('mbfc-data')
            .createSignedUrl(doc.url, 3600); // 1 hour expiry

          if (signedUrlError || !data?.signedUrl) {
            console.warn('Error getting signed URL:', signedUrlError);
            return null;
          }

          return {
            id: doc.id,
            name: doc.name,
            type: doc.type,
            url: data.signedUrl,
            size: doc.size,
            uploadedAt: doc.uploaded_at
          };
        } catch (error) {
          console.error('Error processing document:', error);
          return null;
        }
      })
    );

    // Filter out null documents
    const validDocuments = documents.filter(doc => doc !== null);

    // Build sessionPlans map from session_squads focus
    const sessionPlans = sessionSquads.reduce<Record<string, string>>((acc, ss) => {
      if (ss.focus) {
        acc[ss.squads.name] = ss.focus;
      }
      return acc;
    }, {});

    return {
      id: session.id,
      date: session.date,
      time: session.time,
      duration: session.duration,
      location: session.location,
      type: session.session_type,
      groundBooking: session.ground_booking,
      squadStats,
      sessionPlans,
      trialPlayers: (session as any).trial_players || [],
      documents: validDocuments
    };
  } catch (error) {
    console.error('Error in getSessionSummaryStats:', error);
    throw error;
  }
};

// Get sessions for planning report
export const getSessionsForPlanningReport = async (
  startDate: string,
  endDate: string,
  selectedSquads: Squad[]
): Promise<SessionDetails[]> => {
  try {
    // Get squad IDs first
    const { data: squadData, error: squadError } = await supabase
      .from('squads')
      .select('id, name')
      .in('name', selectedSquads);

    if (squadError) {
      console.error('Error fetching squad IDs:', squadError);
      throw squadError;
    }

    if (!squadData) {
      throw new Error('No squads found');
    }

    const squadIds = squadData.map(s => s.id);

    // Get sessions with related data
    const { data: sessions, error } = await supabase
      .from('sessions')
      .select(`
        *,
        session_squads!inner (
          squad_id,
          squads!inner (
            id,
            name,
            display_name
          ),
          focus
        )
      `)
      .gte('date', startDate)
      .lte('date', endDate)
      .in('session_squads.squad_id', squadIds)
      .order('date', { ascending: true });

    if (error) {
      console.error('Error fetching sessions:', error);
      throw error;
    }

    if (!sessions) {
      return [];
    }

    // Transform the data to match our SessionDetails type
    return sessions.map(session => ({
      id: session.id,
      date: session.date,
      time: session.time,
      duration: session.duration,
      location: session.location,
      type: session.session_type,
      groundBooking: session.ground_booking,
      squads: (session.session_squads as SessionSquadJoin[]).map(ss => ss.squads.name as Squad),
      attendance: emptySquadAttendance(),
      coaches: emptySquadCoaches(),
      focus: (session.session_squads as SessionSquadJoin[]).reduce((acc: Record<Squad, string>, ss) => {
        if (ss.squads?.name) {
          acc[ss.squads.name as Squad] = ss.focus || '';
        }
        return acc;
      }, emptySquadFocus()),
      documents: [],
      attendanceStatus: session.attendance_status,
      locked: session.locked,
      trialPlayers: session.trial_players || []
    }));
  } catch (error) {
    console.error('Error in getSessionsForPlanningReport:', error);
    throw error;
  }
};

// Get sessions for attendance report
export const getSessionsForAttendanceReport = async (
  startDate: string,
  endDate: string,
  selectedSquads: Squad[]
): Promise<unknown[]> => {
  try {
    // Get squad IDs first
    const { data: squadData, error: squadError } = await supabase
      .from('squads')
      .select('id, name')
      .in('name', selectedSquads);

    if (squadError) {
      console.error('Error fetching squad IDs:', squadError);
      throw squadError;
    }

    if (!squadData) {
      throw new Error('No squads found');
    }

    const squadIds = squadData.map(s => s.id);

    // Get sessions with attendance data
    const { data: sessions, error } = await supabase
      .from('sessions')
      .select(`
        *,
        session_squads!inner (
          squad_id,
          squads!inner (
            id,
            name,
            display_name
          )
        ),
        attendance!inner (
          id,
          player_id,
          squad_id,
          present,
          comments,
          players (
            id,
            name
          )
        )
      `)
      .gte('date', startDate)
      .lte('date', endDate)
      .in('session_squads.squad_id', squadIds)
      .eq('attendance_status', 'marked')
      .order('date', { ascending: true });

    if (error) {
      console.error('Error fetching sessions:', error);
      throw error;
    }

    return sessions || [];
  } catch (error) {
    console.error('Error in getSessionsForAttendanceReport:', error);
    throw error;
  }
};

// Get coach attendance statistics
export const getCoachAttendanceStats = async (
  startDate: string,
  endDate: string,
  coachId?: string
): Promise<{ sessions: unknown[]; coachStats: unknown[] }> => {
  try {
    let query = supabase
      .from('sessions')
      .select(`
        *,
        session_coaches!inner (
          squad_id,
          primary_coach_id,
          secondary_coach_id,
          squads!inner (
            id,
            name,
            display_name
          )
        )
      `)
      .gte('date', startDate)
      .lte('date', endDate)
      .eq('attendance_status', 'marked');

    if (coachId) {
      query = query.or(`session_coaches.primary_coach_id.eq.${coachId},session_coaches.secondary_coach_id.eq.${coachId}`);
    }

    const { data: sessions, error } = await query.order('date', { ascending: true });

    if (error) {
      console.error('Error fetching coach attendance:', error);
      throw error;
    }

    type CoachStatEntry = { squad: string; displayName: string; sessions: number; primarySessions: number; secondarySessions: number; totalHours: number };
    // Calculate coach statistics
    const coachStats = (sessions ?? []).reduce((stats: Record<string, CoachStatEntry>, session) => {
      (session.session_coaches as SessionCoachJoin[]).forEach(sc => {
        if (sc.primary_coach_id === coachId || sc.secondary_coach_id === coachId) {
          const role = sc.primary_coach_id === coachId ? 'Primary' : 'Secondary';
          if (!stats[sc.squad_id]) {
            stats[sc.squad_id] = {
              squad: sc.squads.name,
              displayName: sc.squads.display_name,
              sessions: 0,
              primarySessions: 0,
              secondarySessions: 0,
              totalHours: 0
            };
          }
          stats[sc.squad_id].sessions++;
          if (role === 'Primary') {
            stats[sc.squad_id].primarySessions++;
          } else {
            stats[sc.squad_id].secondarySessions++;
          }
          stats[sc.squad_id].totalHours += session.duration;
        }
      });
      return stats;
    }, {});

    return {
      sessions,
      coachStats: Object.values(coachStats)
    };
  } catch (error) {
    console.error('Error in getCoachAttendanceStats:', error);
    throw error;
  }
};

// Get player attendance statistics
export const getPlayerAttendanceStats = async (
  sessionId: string,
  squad: Squad
): Promise<unknown[]> => {
  try {
    // Get squad ID first
    const { data: squadData, error: squadError } = await supabase
      .from('squads')
      .select('id')
      .eq('name', squad)
      .single();

    if (squadError) {
      console.error('Error fetching squad ID:', squadError);
      throw squadError;
    }

    if (!squadData) {
      throw new Error(`Squad not found: ${squad}`);
    }

    // Get attendance records for this session and squad
    const { data: attendance, error } = await supabase
      .from('attendance')
      .select(`
        *,
        players (
          id,
          name
        )
      `)
      .eq('session_id', sessionId)
      .eq('squad_id', squadData.id);

    if (error) {
      console.error('Error fetching player attendance:', error);
      throw error;
    }

    return attendance || [];
  } catch (error) {
    console.error('Error in getPlayerAttendanceStats:', error);
    throw error;
  }
};